/**
 * PatientDetailModal Component
 * 
 * Comprehensive 360° view of a single patient's costs, services, and care management options.
 * Updated for Strategy Redesign Specification v1.1:
 * - Cost rank and high-cost indicator
 * - Risk flags section (Readmissions Watch)
 * - CDM tier assignment
 * - Updated recommended actions (CDM instead of CCM/RPM)
 * 
 * @see MIPS_Cost_Dashboard_Strategy_Redesign_Specification_v1.1, Part 5
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  Check,
  X,
  User,
  Calendar,
  Activity,
  DollarSign,
  Zap,
  Save,
  Minus,
  HeartPulse,
  TrendingUp,
  Shield,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface PatientStrategy {
  id: string;
  name: string;
  shortName: string;
  reason: string;
}

interface ServiceBreakdown {
  category: string;
  description: string;
  cost: number;
  nationalAvg: number | null;
  variancePct: number | null;
  status: 'efficient' | 'acceptable' | 'above_benchmark' | 'no_benchmark';
}

interface PatientDetail {
  mbi: string;
  gender: string;
  dob: string;
  dateOfDeath?: string | null;
  age: number;
  hccRisk: number;
  hccPercentile: number;
  totalCost: number;
  tpccCost?: number;
  nationalAvg: number;
  excessCost: number;
  measures: string[];
  measureCount: number;
  hasReadmission: boolean;
  hasSNF: boolean;
  chronicConditionCount: number | { count: number; conditions: string[]; hasHForCOPD: boolean };
  strategies: PatientStrategy[];
  serviceBreakdown: ServiceBreakdown[];
  notes: string;
  strategyStatuses: Record<string, string>;
  exclusions: string[];
  // New fields from strategy redesign
  costRank?: number;
  isHighCost?: boolean;
  cdmTier?: number | null;
  riskFlags?: string[];
}

interface PatientDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientMbi: string | null;
  practiceId: string;
  npi?: string;
  onStrategyRemoved?: (mbi: string, strategyId: string) => void;
}

// ============================================
// Additional Actions (manual options)
// ============================================

const ADDITIONAL_ACTIONS = [
  { id: 'care_coord', label: 'Flag for care coordinator review' },
  { id: 'post_dc', label: 'Schedule post-discharge follow-up' },
  { id: 'med_review', label: 'Review medication adherence' },
  { id: 'sdoh', label: 'Assess social determinants of health' },
];

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'efficient':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'acceptable':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'above_benchmark':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    default:
      return <Minus className="h-4 w-4 text-gray-400" />;
  }
}

function getStatusLabel(status: string, variancePct: number | null): string {
  if (status === 'no_benchmark') return '—';
  if (variancePct === null) return '—';
  const sign = variancePct >= 0 ? '+' : '';
  return `${sign}${variancePct.toFixed(0)}%`;
}

// ============================================
// Component
// ============================================

export function PatientDetailModal({
  isOpen,
  onClose,
  patientMbi,
  practiceId,
  npi,
  onStrategyRemoved,
}: PatientDetailModalProps) {
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  // Error is keyed by MBI so a stale error from a previous patient never paints
  const [error, setError] = useState<{ mbi: string; message: string } | null>(null);
  const [notes, setNotes] = useState('');
  const [additionalActions, setAdditionalActions] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [pendingExclusions, setPendingExclusions] = useState<Set<string>>(new Set());

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Load patient data when modal opens
  useEffect(() => {
    if (isOpen && patientMbi) {
      loadPatientData();
    }
  }, [isOpen, patientMbi]);

  const loadPatientData = async () => {
    if (!patientMbi) return;
    
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/patient/${patientMbi}`);
      if (npi) url.searchParams.set('npi', npi);
      
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load patient: ${response.status}`);
      }
      
      const data = await response.json();
      setPatient(data);
      setNotes(data.notes || '');
      setPendingExclusions(new Set(data.exclusions || []));
    } catch (err) {
      console.error('Error loading patient detail:', err);
      setError({ mbi: patientMbi, message: 'Failed to load patient details' });
    }
  };

  const handleSave = async () => {
    if (!patient) return;
    
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Save notes
      await fetch(`${apiUrl}/api/practices/${practiceId}/cost/patient-notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mbi: patient.mbi, notes }),
      });
      
      // Process exclusions
      const originalExclusions = new Set(patient.exclusions || []);
      
      // Add new exclusions
      for (const strategyId of pendingExclusions) {
        if (!originalExclusions.has(strategyId)) {
          await fetch(`${apiUrl}/api/practices/${practiceId}/cost/patient-exclusions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ mbi: patient.mbi, strategyId }),
          });
          
          if (onStrategyRemoved) {
            onStrategyRemoved(patient.mbi, strategyId);
          }
        }
      }
      
      // Remove exclusions that were un-checked (re-include patient)
      for (const strategyId of originalExclusions) {
        if (!pendingExclusions.has(strategyId)) {
          await fetch(`${apiUrl}/api/practices/${practiceId}/cost/patient-exclusions`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ mbi: patient.mbi, strategyId }),
          });
        }
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving patient data:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStrategyToggle = (strategyId: string, checked: boolean) => {
    setPendingExclusions(prev => {
      const next = new Set(prev);
      if (checked) {
        // Unchecking = excluding from strategy
        next.add(strategyId);
      } else {
        // Checking = keeping in strategy
        next.delete(strategyId);
      }
      return next;
    });
  };

  const toggleAdditionalAction = (actionId: string) => {
    setAdditionalActions(prev => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  // Calculate metrics
  const multiplier = patient && patient.nationalAvg > 0 
    ? (patient.totalCost / patient.nationalAvg).toFixed(1) 
    : '—';

  // Get CDM tier info
  const getCdmTierInfo = (tier: number | null | undefined) => {
    if (tier === 2) {
      return {
        label: 'Tier 2: CCM + RPM',
        description: 'Enroll in CCM with connected device monitoring',
        icon: HeartPulse,
        color: 'bg-purple-100 text-purple-800 border-purple-300',
      };
    }
    if (tier === 1) {
      return {
        label: 'Tier 1: CCM',
        description: 'Enroll in Chronic Care Management',
        icon: Activity,
        color: 'bg-blue-100 text-blue-800 border-blue-300',
      };
    }
    return null;
  };

  const cdmTierInfo = patient?.cdmTier ? getCdmTierInfo(patient.cdmTier) : null;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden">
        {/* Render is derived from whether the LOADED data matches the REQUESTED
            MBI — never from isLoading timing. The fetch starts in an effect
            (after first paint), so keying on a loading flag painted one frame
            of empty/previous-patient content — the "modal flash". */}
        {error && error.mbi === patientMbi ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error.message}</p>
          </div>
        ) : patient && patient.mbi === patientMbi ? (
          <>
            {/* Header - Dark Background */}
            <DialogHeader className="bg-slate-900 text-white -m-6 mb-0 p-6 rounded-t-lg">
              <div className="flex items-start justify-between">
                <div>
                  <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Patient: {patient.mbi}
                    {/* Cost Rank & High-Cost Badge */}
                    {patient.costRank && (
                      <Badge variant="secondary" className="bg-slate-700 text-slate-200 ml-2">
                        #{patient.costRank}
                      </Badge>
                    )}
                    {patient.isHighCost && (
                      <Badge className="bg-amber-500 text-white ml-1">
                        High-Cost
                      </Badge>
                    )}
                  </DialogTitle>
                  <div className="flex items-center gap-4 mt-2 text-slate-300 text-sm">
                    <span>{patient.age}{patient.gender === 'M' ? 'M' : 'F'}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      DOB: {formatDate(patient.dob)}
                    </span>
                    {patient.dateOfDeath && (
                      <span className="text-slate-400">
                        Deceased: {formatDate(patient.dateOfDeath)}
                      </span>
                    )}
                    <span>HCC: {patient.hccRisk?.toFixed(2) || '—'}</span>
                    {patient.hccPercentile > 0 && (
                      <span>({patient.hccPercentile}th %ile)</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      {typeof patient.chronicConditionCount === 'object' 
                        ? patient.chronicConditionCount?.count || 0 
                        : patient.chronicConditionCount || 0} conditions
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-slate-800">
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Episode Cost</p>
                  <p className="text-lg font-bold">{formatCurrency(patient.totalCost)}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">National Avg</p>
                  <p className="text-lg font-bold">{formatCurrency(patient.nationalAvg)}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Opportunity</p>
                  <p className="text-lg font-bold text-amber-400">
                    {formatCurrency(Math.max(0, patient.excessCost))}
                  </p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">Multiplier</p>
                  <p className="text-lg font-bold">{multiplier}x</p>
                </div>
              </div>

              {/* Badges: Measures + Risk Flags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {patient.measures.map(m => (
                  <Badge key={m} variant="secondary" className="bg-slate-700 text-slate-200">
                    {m.replace('COST_', '').replace('_1', '')}
                  </Badge>
                ))}
                {patient.hasReadmission && (
                  <Badge className="bg-red-600 text-white flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Readmissions Watch
                  </Badge>
                )}
                {patient.hasSNF && (
                  <Badge className="bg-amber-600">SNF</Badge>
                )}
                {cdmTierInfo && (
                  <Badge className={cdmTierInfo.color}>
                    <cdmTierInfo.icon className="h-3 w-3 mr-1" />
                    {cdmTierInfo.label}
                  </Badge>
                )}
              </div>
            </DialogHeader>

            {/* Content */}
            <div className="grid grid-cols-2 gap-6 mt-6">
              {/* Left: Service Breakdown */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Episode Service Breakdown
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  With benchmark context
                </p>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 font-medium">Category</th>
                        <th className="text-right p-2 font-medium">You</th>
                        <th className="text-right p-2 font-medium">Nat'l</th>
                        <th className="text-center p-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patient.serviceBreakdown.length > 0 ? (
                        patient.serviceBreakdown.map((service, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2 truncate max-w-[150px]" title={service.description}>
                              {service.description || service.category}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {formatCurrency(service.cost)}
                            </td>
                            <td className="p-2 text-right font-mono text-muted-foreground">
                              {service.nationalAvg ? formatCurrency(service.nationalAvg) : '—'}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {getStatusIcon(service.status)}
                                <span className={
                                  service.status === 'above_benchmark' ? 'text-red-600' :
                                  service.status === 'efficient' ? 'text-green-600' : 'text-muted-foreground'
                                }>
                                  {getStatusLabel(service.status, service.variancePct)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-4 text-center text-muted-foreground">
                            No service breakdown available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-red-500" />
                    Above benchmark (20%+)
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-green-500" />
                    At or below benchmark
                  </span>
                </div>

                {/* Risk Flags Section */}
                {(patient.hasReadmission || (patient.riskFlags && patient.riskFlags.length > 0)) && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" />
                      Risk Flags
                    </h3>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      {patient.hasReadmission && (
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-amber-900">Readmissions Watch</p>
                            <p className="text-xs text-amber-700">
                              Patient has readmission history. Prioritize for care management enrollment
                              to reduce future readmission risk.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Care Management Actions */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Care Management Actions
                </h3>

                {/* CDM Tier Assignment (if eligible) */}
                {cdmTierInfo && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <cdmTierInfo.icon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-blue-900">{cdmTierInfo.label}</p>
                        <p className="text-sm text-blue-700">{cdmTierInfo.description}</p>
                        <p className="text-xs text-blue-600 mt-1">
                          {patient.cdmTier === 2 
                            ? 'Reimbursement: $150-200/month (CCM + RPM combined)' 
                            : 'Reimbursement: $62-89/month'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Strategy-Matched Actions */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Strategy-Matched
                  </p>
                  <div className="space-y-2">
                    {patient.strategies.length > 0 ? (
                      patient.strategies.map(strategy => {
                        const isExcluded = pendingExclusions.has(strategy.id);
                        // Map legacy ccm/rpm to cdm for display
                        const displayId = (strategy.id === 'ccm' || strategy.id === 'rpm') ? 'cdm' : strategy.id;
                        const displayLabel = getStrategyActionLabel(displayId, patient.cdmTier);
                        
                        return (
                          <div
                            key={strategy.id}
                            className={`border rounded-lg p-3 ${isExcluded ? 'opacity-50 bg-muted' : 'bg-background'}`}
                          >
                            <div className="flex items-start gap-2">
                              <Checkbox
                                checked={!isExcluded}
                                onCheckedChange={(checked) => handleStrategyToggle(strategy.id, !checked)}
                              />
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {displayLabel}
                                </p>
                                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                                  <Zap className="h-3 w-3" />
                                  Auto-flagged: {strategy.reason}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        No strategy matches for this patient
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Actions */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Additional Actions
                  </p>
                  <div className="space-y-2">
                    {ADDITIONAL_ACTIONS.map(action => (
                      <div key={action.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={additionalActions.has(action.id)}
                          onCheckedChange={() => toggleAdditionalAction(action.id)}
                        />
                        <span className="text-sm">{action.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Notes
                  </p>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this patient..."
                    className="h-24 text-sm"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSave} disabled={isSaving} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Loading skeleton — mirrors the real layout (dark header + 2-col
             body) so the swap to content doesn't shift the dialog. */
          <>
            <div className="bg-slate-900 -m-6 mb-0 p-6 rounded-t-lg space-y-4">
              <Skeleton className="h-7 w-64 bg-slate-700" />
              <Skeleton className="h-4 w-80 bg-slate-800" />
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 bg-slate-800" />)}
              </div>
              <Skeleton className="h-6 w-48 bg-slate-800" />
            </div>
            <div className="grid grid-cols-2 gap-6 mt-6">
              <div className="space-y-3">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-64 w-full" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Helper: Strategy action labels (updated for CDM)
// ============================================

function getStrategyActionLabel(strategyId: string, cdmTier?: number | null): string {
  const labels: Record<string, string> = {
    cdm: cdmTier === 2 
      ? 'Enroll in Chronic Disease Management (CCM + RPM)' 
      : 'Enroll in Chronic Disease Management (CCM)',
    pcm: 'Enroll in Principal Care Management (PCM)',
    snf: 'SNF Optimization review',
    ed: 'ED Diversion program',
    medopt: 'Medication Optimization review',
    bh: 'Behavioral Health Integration',
    // Legacy mappings
    ccm: 'Enroll in Chronic Disease Management',
    rpm: 'Add Remote Patient Monitoring',
  };
  return labels[strategyId] || strategyId;
}

export default PatientDetailModal;
